import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  getDashboardInsights, getTransactions, formatCurrency, MONTHS, TYPE_COLORS,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  UserCircle, Lock, Plus, ChevronLeft, ChevronRight, RefreshCw, ArrowUpRight,
  TrendingUp, TrendingDown, Wallet, PiggyBank,
} from 'lucide-react';
import EmptyState from '../components/EmptyState';

const PersonalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [insights, setInsights] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [insRes, txRes] = await Promise.all([
        getDashboardInsights(month, year, 'personal'),
        getTransactions({ month, year, scope: 'personal' }),
      ]);
      setInsights(insRes.data);
      setTransactions(txRes.data);
    } catch (e) {
      toast.error('Errore caricamento area personale');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  const changeMonth = (d) => {
    let m = month + d, y = year;
    if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const totalsByType = useMemo(
    () => insights?.totals || { REDDITO: 0, SPESE: 0, RISPARMI: 0, DEBITI: 0 },
    [insights]
  );
  const balance = insights?.balance ?? 0;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-muted rounded-xl" />)}
        </div>
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="personal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium mb-2">
            <Lock className="w-3 h-3" /> Solo tu vedi questi dati
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <UserCircle className="w-7 h-7 text-primary" strokeWidth={1.5} />
            Area personale
          </h1>
          <p className="text-muted-foreground">
            Movimenti privati di {user?.name} — non condivisi con la famiglia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-3 py-2 bg-card border rounded-lg min-w-[180px] text-center font-medium">
            {MONTHS[month - 1]} {year}
          </div>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI cards (solo personali) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="card-hover">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Reddito</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{formatCurrency(totalsByType.REDDITO || 0)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Spese</p>
                <p className="text-2xl font-bold tabular-nums text-red-500">{formatCurrency(totalsByType.SPESE || 0)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Risparmi</p>
                <p className="text-2xl font-bold tabular-nums text-blue-500">{formatCurrency(totalsByType.RISPARMI || 0)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Saldo</p>
                <p className={`text-2xl font-bold tabular-nums ${balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                  {formatCurrency(balance)}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${balance >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                <Wallet className={`w-5 h-5 ${balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transazioni private */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Transazioni private del mese</CardTitle>
          <Button onClick={() => navigate('/transactions?add=true&private=true')} size="sm">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <EmptyState
              icon={Lock}
              title="Nessuna transazione privata in questo mese"
              description="Le transazioni che marchi come 'Privato' nel form compaiono solo qui. Sono invisibili agli altri membri della famiglia."
              action={{
                label: 'Aggiungi transazione privata',
                icon: Plus,
                onClick: () => navigate('/transactions?add=true&private=true'),
              }}
            />
          ) : (
            <div className="space-y-1">
              {transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/40 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${
                      t.type === 'REDDITO' ? 'bg-emerald-500' :
                      t.type === 'SPESE' ? 'bg-red-500' :
                      t.type === 'RISPARMI' ? 'bg-blue-500' : 'bg-orange-500'
                    }`} />
                    <Lock className="w-3 h-3 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{t.category_name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {t.description || t.date}
                      </p>
                    </div>
                  </div>
                  <span className={`font-semibold tabular-nums shrink-0 ml-2 ${
                    t.type === 'REDDITO' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
                  }`}>
                    {t.type === 'REDDITO' ? '+' : '-'}{formatCurrency(t.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info card */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-5 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span>
              I dati di questa pagina sono <strong className="text-foreground">visibili solo a te</strong>.
              Non sono inclusi nella dashboard di famiglia, nei budget condivisi o nei report degli altri membri.
              Puoi rendere private nuove transazioni dal form "Nuova transazione" attivando lo switch dedicato.
            </span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PersonalPage;
